import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { Tooltip } from 'ui-components/components/tooltip';
import { testId } from 'ui-components/services/testid-service';

import { TrackingStatusIndicator } from '../tracking-status-indicator';
import styles from './shipment-status-label.module.less';
import { UserDateFormat } from '../user-date-format/index';

/**
 * @typedef {import('./shipment-status-types').ShipmentStatus} ShipmentStatus
 * @typedef {import('./shipment-status-types').RefundAssistStatus} RefundAssistStatus
 */

const messages = defineMessages({
  unshipped: {
    description: 'shipmentStatusLabel.awaitingShipment',
    defaultMessage: 'Awaiting Shipment',
    id: 'PS7EOD',
  },
  shipped: {
    description: 'shipmentStatusLabel.shipped',
    defaultMessage: 'Shipped',
    id: 'SBY8sc',
  },
  cancelled: {
    description: 'shipmentStatusLabel.cancelled',
    defaultMessage: 'Cancelled',
    id: 'aTG6LS',
  },
  onhold: {
    description: 'shipmentStatusLabel.onHold',
    defaultMessage: 'On Hold',
    id: '7AZcm4',
  },
  awaitingpayment: {
    description: 'shipmentStatusLabel.awaitingPayment',
    defaultMessage: 'Awaiting Payment',
    id: 'Eitvrw',
  },
  pendingfulfillment: {
    description: 'shipmentStatusLabel.pendingFulfillment',
    defaultMessage: 'Pending Fulfillment',
    id: 'jkqVdP',
  },
  rejectedfulfillment: {
    description: 'shipmentStatusLabel.rejectedFulfillment',
    defaultMessage: 'Rejected Fulfillment',
    id: '2N283z',
  },
  pendingpickup: {
    description: 'shipmentStatusLabel.pendingPickup',
    defaultMessage: 'Pending Pickup',
    id: 'P3S+N2',
  },
  // returns
  returned: {
    description: 'shipmentStatusLabel.returned',
    defaultMessage: 'Outstanding',
    id: 'Rfkz/r',
  },
  outstanding: {
    description: 'shipmentStatusLabel.outstanding',
    defaultMessage: 'Outstanding',
    id: 'kGeweW',
  },
  received: {
    description: 'shipmentStatusLabel.received',
    defaultMessage: 'Received',
    id: 'TC/S7M',
  },
  allocated: {
    description: 'shipmentStatusLabel.allocated',
    defaultMessage: 'Allocated',
    id: 'GqXnDP',
  },
  reshipped: {
    description: 'shipmentStatusLabel.reshipped',
    defaultMessage: 'Reshipped',
    id: '+slouC',
  },
  // refund assist statuses
  refundeligible: {
    description: 'Status label indicating the refund request is eligible',
    defaultMessage: 'Eligible',
    id: 'eVrN2e',
  },
  refundscheduled: {
    description: 'Status label indicating the refund request has been scheduled',
    defaultMessage: 'Request Scheduled',
    id: 'XvE5ec',
  },
  refundpending: {
    description: 'Status label indicating the refund is pending',
    defaultMessage: 'Refund Pending',
    id: '14BwbC',
  },
  refunded: {
    description: 'Status label indicating the refund has been completed',
    defaultMessage: 'Refunded',
    id: 'SjK4nx',
  },
  refundrejected: {
    description: 'Status label indicating the refund request has been rejected',
    defaultMessage: 'Refund Rejected',
    id: 'r7DCdu',
  },
  refundcanceled: {
    description: 'Status label indicating the refund request has been canceled',
    defaultMessage: 'Refund Canceled',
    id: 'CA2qnj',
  },
  refunddisabled: {
    description: 'Status label indicating the refund has been disabled',
    defaultMessage: 'Disabled',
    id: 'DQ/pGe',
  },
  date: {
    description: 'shipmentStatusLabel.date',
    defaultMessage: 'Date',
    id: 'fmExQx',
  },
  tracking: {
    description: 'shipmentStatusLabel.tracking',
    defaultMessage: 'Tracking',
    id: 'SZu0DJ',
  },
});

const getStatusText = (status, intl) => {
  if (status === '') {
    return intl.formatMessage(messages.unshipped);
  }
  if (messages[status]) {
    return intl.formatMessage(messages[status]);
  }
  return null;
};

const getStatusTextIcon = (status) => {
  switch (status) {
    case 'shipped':
      return null;
    case 'returned':
      return <FontAwesomeIcon className={styles['status-icon']} icon={faArrowLeft} />;
    case 'received':
      return <FontAwesomeIcon className={styles['status-icon']} icon={faArrowLeft} />;
    case 'allocated':
      return <FontAwesomeIcon className={styles['status-icon']} icon={faLock} />;
    case 'reshipped':
      return <FontAwesomeIcon className={styles['status-icon']} icon={faArrowRight} />;
    case 'outstanding':
      return <FontAwesomeIcon className={styles['status-icon']} icon={faArrowLeft} />;
    // refund assist statuses
    case 'refundscheduled':
    case 'refundpending':
    case 'refunded':
    case 'refundrejected':
    case 'refundcanceled':
      return null;
    default:
      return null;
  }
};

const StatusDetails = ({
  date = '',
  trackingCodeText = '',
  truncated = false,
  standalone = false,
  pill = false,
  trackingUrl = '',
  trackingStatus = '',
}) => {
  const intl = useIntl();
  if (pill || standalone) {
    return null;
  }
  if (truncated) {
    const toolTipText = (
      <div>
        {date ? (
          <div>
            <strong>{intl.formatMessage(messages.date)}:</strong>
            {date}
          </div>
        ) : null}
        {trackingCodeText ? (
          <div>
            <strong>{intl.formatMessage(messages.tracking)}:</strong> {trackingCodeText}
          </div>
        ) : null}
      </div>
    );
    return (
      <Tooltip position='bottom' text={toolTipText}>
        <button className={styles.button}>&middot;&middot;&middot;</button>
      </Tooltip>
    );
  }
  return (
    <React.Fragment>
      {date ? (
        <div className={styles.date}>
          <UserDateFormat value={date} />
        </div>
      ) : null}
      <div className={styles['tracking-status']}>
        <TrackingStatusIndicator status={trackingStatus} />
      </div>
      {trackingCodeText ? (
        <a className={styles.button} href={trackingUrl} rel='noopener noreferrer' target='_blank'>
          {trackingCodeText}
        </a>
      ) : null}
    </React.Fragment>
  );
};

/**
 * @typedef {object} StatusDetailsProps
 * @property {string} [date=''] - Ship date
 * @property {string} [trackingCodeText=''] - Tracking number
 * @property {string} [trackingUrl=''] - Tracking URL
 * @property {string} [trackingStatus=''] - Tracking status
 * @property {boolean} [truncated=false] - Whether to show truncated view
 * @property {boolean} [standalone=false] - Whether to display as standalone
 * @property {boolean} [pill=false] - Whether to display as pill only
 */

/**
 * Renders a single status tag with pill, text, and optional children
 * @param {string} status - Status for styling and text
 * @param {string} testIdSuffix - Suffix for test ID
 * @param {React.ReactNode} children - Optional children (like StatusDetails)
 * @param {object} intl - React-intl object
 * @returns {React.ReactElement} Status tag JSX
 */
const renderStatusTag = (status, testIdSuffix, children, intl) => (
  <div className={styles[status]}>
    <div className={styles['status-label-wrapper']}>
      <div className={styles.pill} />
      <div className={styles['status-text']} {...testId(`${testIdSuffix}-label`)}>
        {getStatusTextIcon(status)}
        {getStatusText(status, intl)}
      </div>
      {children}
    </div>
  </div>
);

/**
 * @typedef {object} ShipmentStatusLabelProps
 * @property {ShipmentStatus} [status='unshipped'] - The status to display
 * @property {RefundAssistStatus} [refundAssistStatus] - The refund assist status to display as additional tag
 * @property {boolean} [standalone=false] - Whether to display as standalone component
 * @property {boolean} [pill=false] - Whether to display as pill only
 */

/**
 * @param {ShipmentStatusLabelProps & StatusDetailsProps} props
 * @returns {React.ReactElement} Shipment status label component
 */
export const ShipmentStatusLabel = ({ status = 'unshipped', refundAssistStatus, standalone = false, pill = false, ...rest }) => {
  const props = { status, standalone, pill, ...rest };
  const intl = useIntl();
  const statusToLower = status.toLowerCase();
  const isUnshipped = statusToLower === '' || statusToLower === 'unshipped';
  const isFullDisplayMode = !pill;
  const shouldShowRefundAssistStatus = refundAssistStatus && isFullDisplayMode;

  if ((isUnshipped && !standalone) || (statusToLower === 'awaitingpayment' && !standalone)) {
    return null;
  }

  return (
    <div className={styles['dual-status-container']}>
      {renderStatusTag(statusToLower, 'shipment-status', isFullDisplayMode && <StatusDetails {...props} />, intl)}
      {shouldShowRefundAssistStatus ? renderStatusTag(refundAssistStatus, 'refund-assist-status', null, intl) : null}
    </div>
  );
};
